import EXIF from 'exif-js';

/**
 * Get the oriented image based on EXIF data.
 * This function reads the EXIF orientation data from an image file,
 * applies the necessary transformations to the image, and returns a new File object
 * with the image oriented correctly.
 *
 * If the file type does not support EXIF data, it returns the original file.
 *
 * Exif orientation values to correctly display the letter F:
 * 
      1             2
  ██████        ██████
  ██                ██
  ████            ████
  ██                ██
  ██                ██

    3             4
      ██        ██
      ██        ██
    ████        ████
      ██        ██
  ██████        ██████

    5             6
██████████    ██
██  ██        ██  ██
██            ██████████

    7             8
        ██    ██████████
    ██  ██        ██  ██
██████████            ██

 * @param file
 * @returns File
 */
export async function getOrientedImage(file: File): Promise<File> {
  // Check of the file type supports EXIF
  const exifTypes = ['image/jpeg', 'image/jpg', 'image/tiff'];
  if (!exifTypes.includes(file.type)) {
    return file;
  }

  // read the EXIF data from the file
  const arrayBuffer = await file.arrayBuffer();
  const exif = EXIF.readFromBinaryFile(arrayBuffer);
  const orientation = exif.Orientation || 1;

  // read the file as a data URL
  const dataUrl = await new Promise<string>(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });

  // Create a new image element and wait for it to load
  const img = await new Promise<HTMLImageElement>(resolve => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.src = dataUrl;
  });

  // Canvas setup
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const width = img.width;
  const height = img.height;

  // Apply the width and height to the canvas
  if (orientation > 4) {
    canvas.width = height;
    canvas.height = width;
  } else {
    canvas.width = width;
    canvas.height = height;
  }

  // Transform the canvas context
  // See https://jdhao.github.io/2019/07/31/image_rotation_exif_info/
  switch (orientation) {
    // Flip horizontally
    case 2:
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
      break;
    // Rotate 180°
    case 3:
      ctx.translate(width, height);
      ctx.rotate(Math.PI);
      break;
    // Flip vertically
    case 4:
      ctx.translate(0, height);
      ctx.scale(1, -1);
      break;
    // Rotate 90° CW and flip horizontally
    case 5:
      ctx.rotate(0.5 * Math.PI);
      ctx.scale(1, -1);
      break;
    // Rotate 90° CCW
    case 6:
      ctx.translate(0, width);
      ctx.rotate(-0.5 * Math.PI);
      break;
    // Rotate 90° CW and flip horizontally
    case 7:
      ctx.translate(height, 0);
      ctx.rotate(0.5 * Math.PI);
      ctx.scale(-1, 1);
      break;
    // Rotate 90° CCW
    case 8:
      ctx.translate(0, width);
      ctx.rotate(-0.5 * Math.PI);
      break;
    // Normal (no rotation)
    default:
      break;
  }

  ctx.drawImage(img, 0, 0);

  // get a Blob from the canvas
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(b => {
      if (b) {
        resolve(b);
      } else {
        reject(new Error('Canvas toBlob returned null'));
      }
    }, 'image/jpeg'),
  );

  // based on the Blob, create a new File object
  return new File([blob], file.name, { type: file.type });
}
